import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiEdit2, FiSave } from "react-icons/fi";
import {
  fetchTitles,
  updateTitleData,
} from "store/features/content/contentActions";
import {
  selectTitles,
  selectContentLoading,
  selectContentError,
} from "store/features/content/contentSelectors";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

const Title = () => {
  const dispatch = useDispatch();
  const titles = useSelector(selectTitles);
  const loading = useSelector(selectContentLoading);
  const error = useSelector(selectContentError);

  const [isEditing, setIsEditing] = useState(false);
  const [editedTitles, setEditedTitles] = useState({
    home: [],
  });

  useEffect(() => {
    dispatch(fetchTitles());
  }, [dispatch]);

  useEffect(() => {
    if (titles) {
      setEditedTitles({ ...titles });
    }
  }, [titles]);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
    }
  }, [error]);

  const handleInputChange = (index, value) => {
    const newTitles = [...editedTitles.home];
    newTitles[index] = value;
    setEditedTitles({ ...editedTitles, home: newTitles });
  };

  const handleSave = () => {
    dispatch(updateTitleData(editedTitles));
    setIsEditing(false);
    showSuccessToast("Titles updated successfully!");
  };

  const addNewTitle = () => {
    setEditedTitles({
      ...editedTitles,
      home: [...editedTitles.home, ""],
    });
  };

  const removeTitle = (index) => {
    const newTitles = [...editedTitles.home];
    newTitles.splice(index, 1);
    setEditedTitles({ ...editedTitles, home: newTitles });
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-100 p-4 text-red-600">
        Error loading orders: {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 h-screen">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Title Management</h1>
        <div className="mt-4 flex space-x-3 md:mt-0">
          {isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(false)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
              >
                <FiSave className="mr-2" /> Save Changes
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
            >
              <FiEdit2 className="mr-2" /> Edit Titles
            </button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="p-6">
          <h2 className="mb-4 text-lg font-medium text-gray-800">
            Home Page Titles
          </h2>

          {editedTitles.home?.length > 0 ? (
            <div className="space-y-4">
              {editedTitles.home.map((title, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <span className="w-8 text-gray-500">{index + 1}.</span>
                  {isEditing ? (
                    <div className="flex flex-1 space-x-2">
                      <input
                        type="text"
                        value={title}
                        onChange={(e) =>
                          handleInputChange(index, e.target.value)
                        }
                        className="flex-1 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        onClick={() => removeTitle(index)}
                        className="rounded-md bg-red-100 px-3 py-2 text-red-600 hover:bg-red-200"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex-1 rounded-md bg-gray-50 px-3 py-2">
                      {title}
                    </div>
                  )}
                </div>
              ))}
              {isEditing && (
                <button
                  onClick={addNewTitle}
                  className="mt-4 rounded-md bg-green-100 px-4 py-2 text-green-600 hover:bg-green-200"
                >
                  + Add New Title
                </button>
              )}
            </div>
          ) : (
            <div className="rounded-md bg-gray-50 py-8 text-center">
              <p className="text-gray-500">
                {isEditing ? "No titles added yet" : "No titles available"}
              </p>
              {isEditing && (
                <button
                  onClick={addNewTitle}
                  className="mt-4 rounded-md bg-green-100 px-4 py-2 text-green-600 hover:bg-green-200"
                >
                  + Add First Title
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-md border-l-4 border-blue-400 bg-blue-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Tips</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                These titles will be displayed as section headings on your home
                page. Keep them concise and engaging for better user experience.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Title;
